import { useEffect, useState } from "react";
import {
  Game,
  GameData,
  TeamTurns,
  UpdateGameRequestModel,
} from "../../../domain/model/Game";
import { Category } from "../../../domain/model/Category";
import { Answer, Difficulty, Question } from "../../../domain/model/Question";
import { Lifeline, LifelineType } from "../../../domain/model/Lifeline";
import { GetRandomQuestionUseCase } from "../../../domain/usecase/question/getRandomQuestion/GetRandomQuestionUseCase";
import { GetAnswerByQuestionIdUseCase } from "../../../domain/usecase/answer/getByQuestionId/GetByQuestionIdUseCase";
import { State } from "../../../domain/model/ResponseState";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  getDifficultyInfo,
  shouldPlaySounds,
  showGlobalLoadingView,
} from "../../../core/utils/Utils";
import { PlayerAddToHistoryUseCase } from "../../../domain/usecase/player/AddToHistory/AddToHistoryUseCase";
import { QuestionGiveAnswerUseCase } from "../../../domain/usecase/question/giveAnswer/QuestionGiveAnswerUseCase";
import { useNavigate } from "react-router-dom";
import { ROUTE_ENDPOINTS } from "../../../data/constant/RouteConstants";
import { UpdateGameUseCase } from "../../../domain/usecase/game/update/UseCase";
import { PlayerAddToUnfinishedGamesUseCase } from "../../../domain/usecase/player/AddToUnfinishedGames/UseCase";
import SharedPreferences from "../../../core/utils/SharedPreferences";
import { STORAGE_KEYS } from "../../../data/constant/Constants";
import {
  getSoundCompletePath,
  SoundRepository,
  Sounds,
} from "../../../data/repository/sound/SoundRepository";

export function GameViewModel(
  getRandomQuestionUseCase: GetRandomQuestionUseCase,
  getAnswerByQuestionIdUseCase: GetAnswerByQuestionIdUseCase,
  playerAddToHistoryUseCase: PlayerAddToHistoryUseCase,
  questionGiveAnswerUseCase: QuestionGiveAnswerUseCase,
  updateGameUseCase: UpdateGameUseCase,
  playerAddToUnfinishedGamesUseCase: PlayerAddToUnfinishedGamesUseCase
) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [gameData, setGameData] = useState<GameData | undefined>();

  const navigate = useNavigate();

  const [question, setQuestion] = useState<Question | undefined>();
  const [answer, setAnswer] = useState<Answer | undefined>();

  const [selectedCategory, setSelectedCategory] = useState<
    Category | undefined
  >();
  const [selectedDifficulty, setSelectedDifficulty] = useState<
    Difficulty | undefined
  >();

  const [gameDataInitialized, setGameDataInitialized] =
    useState<boolean>(false);

  const [alreadyHasQuestion, setAlreadyHasQuestion] = useState<boolean>(false);
  const [hasGivenRate, setHasGivenRate] = useState<boolean>(false);

  const [currentSelectedIndex, setCurrentSelectedIndex] = useState<number>();
  const [currentQuestionIndexNumber, setCurrentQuestionIndexNumber] =
    useState<number>();
  const soundRepo = new SoundRepository();

  useEffect(() => {
    if (gameDataInitialized && gameData && !gameData.isUnfinished) {
      checkForTieBreakerLifeline();
      setGameDataInitialized(false);
    }
  }, [gameDataInitialized, gameData]);

  function init(gameData: GameData) {
    setGameData(gameData);
    setGameDataInitialized(true);
    SharedPreferences.setBoolean(
      STORAGE_KEYS.PLAY_SOUNDS,
      gameData.shouldPlaySounds ?? true
    );
  }

  function checkForTieBreakerLifeline() {
    if (!gameData) return;
    const firstLifelineTieBreakerIndex = gameData.firstTeamLifelines.findIndex(
      (l) => l.type == LifelineType.TIE_BREAKER
    );
    if (firstLifelineTieBreakerIndex != -1) {
      setLifelineAsDisabled(
        firstLifelineTieBreakerIndex,
        gameData.firstTeamLifelines,
        true
      );
    }

    const secondLifelineTieBreakerIndex =
      gameData.secondTeamLifelines.findIndex(
        (l) => l.type == LifelineType.TIE_BREAKER
      );
    if (secondLifelineTieBreakerIndex != -1) {
      setLifelineAsDisabled(
        secondLifelineTieBreakerIndex,
        gameData.secondTeamLifelines,
        false
      );
    }

    updateGameData({
      firstTeamScore: firstLifelineTieBreakerIndex != -1 ? 200 : 0,
      secondTeamScore: secondLifelineTieBreakerIndex != -1 ? 200 : 0,
    });
  }

  function setLifelineAsDisabled(
    selectedLifelineIndex: number,
    lifelines: Array<Lifeline>,
    isFirstTeam: boolean
  ) {
    const currentLifeline = lifelines[selectedLifelineIndex];
    currentLifeline.disabled = true;

    if (isFirstTeam) {
      updateGameData({ firstTeamLifelines: lifelines });
    } else {
      updateGameData({ secondTeamLifelines: lifelines });
    }
  }

  function addOrDeductPointsFromATeam(
    isFirstTeam: boolean,
    add: boolean,
    point: number = 250
  ) {
    if (!gameData) return;
    const scoreToAddOrDeduct = add ? point : -point;
    if (isFirstTeam) {
      updateGameData({
        firstTeamScore: gameData.firstTeamScore + scoreToAddOrDeduct,
      });
    } else {
      updateGameData({
        secondTeamScore: gameData.secondTeamScore + scoreToAddOrDeduct,
      });
    }
  }

  async function updateResultGame(model: UpdateGameRequestModel) {
    if (!gameData?.resultGame?._id) return;
    const response = await updateGameUseCase.invoke(
      model,
      gameData.resultGame._id
    );
    switch (response.responseState) {
      case State.Success:
        updateGameData({ resultGame: response.data });
        break;
      case State.Fail:
      case State.Error:
        await updateResultGame(model);
        break;
    }
  }

  async function saveGame() {
    if (!gameData) return;
    delete gameData.isUnfinished;
    delete gameData.resultGame;

    const copyGameData: any = structuredClone(gameData);

    const diffMapString = `\"{Difficulty.easy: ${gameData.difficultiesMap.get(
      Difficulty.EASY
    )}, Difficulty.medium: ${gameData.difficultiesMap.get(
      Difficulty.MEDIUM
    )}, Difficulty.hard: ${gameData.difficultiesMap.get(Difficulty.HARD)}}\"`;

    copyGameData.difficultiesMap = diffMapString;

    copyGameData.categories.map((c: any, index: number) => {
      const teamAEasyDiff =
        gameData.categories[index].pointsHashmap
          .get(TeamTurns.teamA)
          ?.get(Difficulty.EASY) == true
          ? "true"
          : "false";
      const teamAMediumDiff =
        gameData.categories[index].pointsHashmap
          .get(TeamTurns.teamA)
          ?.get(Difficulty.MEDIUM) == true
          ? "true"
          : "false";
      const teamAHardDiff =
        gameData.categories[index].pointsHashmap
          .get(TeamTurns.teamA)
          ?.get(Difficulty.HARD) == true
          ? "true"
          : "false";

      const teamBEasyDiff =
        gameData.categories[index].pointsHashmap
          .get(TeamTurns.teamB)
          ?.get(Difficulty.EASY) == true
          ? "true"
          : "false";
      const teamBMediumDiff =
        gameData.categories[index].pointsHashmap
          .get(TeamTurns.teamB)
          ?.get(Difficulty.MEDIUM) == true
          ? "true"
          : "false";
      const teamBHardDiff =
        gameData.categories[index].pointsHashmap
          .get(TeamTurns.teamB)
          ?.get(Difficulty.HARD) == true
          ? "true"
          : "false";

      c.pointsHashmap = `\"{TeamTurns.teamA: {Difficulty.easy: ${teamAEasyDiff}, Difficulty.medium: ${teamAMediumDiff}, Difficulty.hard: ${teamAHardDiff}}, TeamTurns.teamB: {Difficulty.easy: ${teamBEasyDiff}, Difficulty.medium: ${teamBMediumDiff}, Difficulty.hard: ${teamBHardDiff}}}\"`;
    });

    await copyGameData.firstTeamLifelines.map((l: any, index: number) => {
      const currentL = gameData.firstTeamLifelines[index];
      const type = currentL.type;

      switch (type) {
        case LifelineType.CALL_FRIEND:
          l.icon = "assets/images/lifelines/call_a_friend.png";
          break;
        case LifelineType.DARE:
          l.icon = "assets/images/lifelines/dare.png";
          break;
        case LifelineType.TRAP:
          l.icon = "assets/images/lifelines/trap.png";
          break;
        case LifelineType.DOUBLE_POINT:
          l.icon = "assets/images/lifelines/double_points.png";
          break;
        case LifelineType.EXTRA_TIME:
          l.icon = "assets/images/lifelines/extra_time.png";
          break;
        case LifelineType.INSURANCE:
          l.icon = "assets/images/lifelines/insurance.png";
          break;
        case LifelineType.TIE_BREAKER:
          l.icon = "assets/images/lifelines/tie_breaker.png";
          break;
        case LifelineType.TWO_ANSWERS:
          l.icon = "assets/images/lifelines/two_answers.png";
          break;
      }
    });

    await copyGameData.secondTeamLifelines.map((l: any, index: number) => {
      const currentL = gameData.secondTeamLifelines[index];
      const type = currentL.type;

      switch (type) {
        case LifelineType.CALL_FRIEND:
          l.icon = "assets/images/lifelines/call_a_friend.png";
          break;
        case LifelineType.DARE:
          l.icon = "assets/images/lifelines/dare.png";
          break;
        case LifelineType.TRAP:
          l.icon = "assets/images/lifelines/trap.png";
          break;
        case LifelineType.DOUBLE_POINT:
          l.icon = "assets/images/lifelines/double_points.png";
          break;
        case LifelineType.EXTRA_TIME:
          l.icon = "assets/images/lifelines/extra_time.png";
          break;
        case LifelineType.INSURANCE:
          l.icon = "assets/images/lifelines/insurance.png";
          break;
        case LifelineType.TIE_BREAKER:
          l.icon = "assets/images/lifelines/tie_breaker.png";
          break;
        case LifelineType.TWO_ANSWERS:
          l.icon = "assets/images/lifelines/two_answers.png";
          break;
      }
    });

    const gameDataJson = JSON.stringify(copyGameData);

    showGlobalLoadingView(true);
    const response = await playerAddToUnfinishedGamesUseCase.invoke(
      gameDataJson
    );
    showGlobalLoadingView(false);
    switch (response.responseState) {
      case State.Success:
        navigate(ROUTE_ENDPOINTS.HOME_PLAY, { replace: true });
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  async function getRandomQuestion(
    category: Category,
    difficulty: Difficulty,
    index: number
  ) {
    if (!gameData) return;

    setSelectedCategory(category);
    setSelectedDifficulty(difficulty);
    setCurrentSelectedIndex(index);

    const categoryIndex = gameData.categories.findIndex(
      (c) => c.id == category.id
    );
    const currentQuestionIndex = index + categoryIndex * 6;
    setCurrentQuestionIndexNumber(currentQuestionIndex);

    var localQuestion: Question | undefined;
    var localAnswer: Answer | undefined;

    const resultGame = gameData.resultGame;

    if (resultGame) {
      if (
        resultGame.questionsList &&
        resultGame.questionsList.length == 36 &&
        resultGame.answersList &&
        resultGame.answersList.length == 36
      ) {
        localQuestion = resultGame.questionsList[currentQuestionIndex];
        setAlreadyHasQuestion(localQuestion != null);
        localAnswer = resultGame.answersList[currentQuestionIndex];
      }
      if (
        resultGame.hasGivenRateBoolList &&
        resultGame.hasGivenRateBoolList.length == 36
      ) {
        setHasGivenRate(resultGame.hasGivenRateBoolList[currentQuestionIndex]);
      }
    }

    if (!localQuestion || !localAnswer) {
      setLoading(true);

      const response = await getRandomQuestionUseCase.invoke({
        categoryId: category.id,
        difficulty: difficulty,
        subcategories: category.subcategories
          .filter((s) => s.isSelected)
          .flatMap((s) => s.title),
      });
      switch (response.responseState) {
        case State.Success:
          if (response.data) {
            localQuestion = response.data;

            // testing question type 1 and 2
            // if (question.attachment) {
            //   question.type = QuestionType.TYPE_1;
            //   question.attachment.type = AttachmentType.VIDEO;
            //   question.attachment.downloadUrl =
            //     "https://download.samplelib.com/mp4/sample-5s.mp4";
            // }

            if (localQuestion._id) {
              localAnswer = await getAnswerByQuestionId(localQuestion._id);
            }
          }
          break;
        case State.Fail:
        case State.Error:
          setLoading(false);
          showSnackbar(response.error?.message);
          return;
      }

      if (!localQuestion || !localAnswer) return;
      updateResultGame({
        action: "Update Questions List",
        index: currentQuestionIndex,
        questionId: localQuestion._id,
        answerId: localAnswer._id,
      });
    }

    setQuestion(localQuestion);
    setAnswer(localAnswer);
  }

  async function getAnswerByQuestionId(
    questionId: string
  ): Promise<Answer | undefined> {
    const response = await getAnswerByQuestionIdUseCase.invoke(questionId);
    switch (response.responseState) {
      case State.Success:
        setAnswer(response.data);
        setLoading(false);
        return response.data;
      case State.Fail:
      case State.Error:
        await getAnswerByQuestionId(questionId);
        break;
    }
    setLoading(false);
  }

  async function OnQuestionAnswered({
    who,
    hasGivenRate,
  }: {
    who?: string;
    hasGivenRate: boolean;
  }) {
    if (!gameData || !selectedDifficulty) return;

    var isAnsweredCorrectly = true;
    var pointToAdd = 0;
    var pointToDeduct = 0;

    var selectedLifelineType;

    const currentLifelines =
      gameData.currentTeamTurn == TeamTurns.teamA
        ? gameData.firstTeamLifelines
        : gameData.secondTeamLifelines;

    const selectedLifelineIndex = currentLifelines.findIndex(
      (lifeline) => !lifeline.disabled && lifeline.selected
    );
    if (selectedLifelineIndex != -1) {
      const currentLifeline = currentLifelines[selectedLifelineIndex];

      setLifelineAsDisabled(
        selectedLifelineIndex,
        currentLifelines,
        gameData.currentTeamTurn == TeamTurns.teamA
      );
      selectedLifelineType = currentLifeline.type;
      switch (currentLifeline.type) {
        case LifelineType.DOUBLE_POINT:
          pointToAdd = getDifficultyInfo(selectedDifficulty, t).point;
          break;
        case LifelineType.TRAP:
          pointToDeduct = getDifficultyInfo(selectedDifficulty, t).point;
          break;
        default:
          break;
      }
    }

    if (who != undefined) {
      var pointsToAdd = getDifficultyInfo(selectedDifficulty, t).point;
      if (who == gameData.firstTeamName) {
        if (gameData.currentTeamTurn == TeamTurns.teamA) {
          pointsToAdd += pointToAdd;
        } else {
          pointToDeduct = 0;
        }
        updateGameData({
          firstTeamScore: gameData.firstTeamScore + pointsToAdd,
          secondTeamScore: gameData.secondTeamScore - pointToDeduct,
        });
      } else if (who == gameData.secondTeamName) {
        if (gameData.currentTeamTurn == TeamTurns.teamB) {
          pointsToAdd += pointToAdd;
        } else {
          pointToDeduct = 0;
        }
        updateGameData({
          secondTeamScore: gameData.secondTeamScore + pointsToAdd,
          firstTeamScore: gameData.firstTeamScore - pointToDeduct,
        });
      }
    } else {
      isAnsweredCorrectly = false;
    }
    markPointAsCompleted(
      gameData.canAnyTeamChooseAnyQuestion
        ? currentSelectedIndex! < 3
          ? gameData.currentTeamTurn == TeamTurns.teamB
            ? TeamTurns.teamA
            : gameData.currentTeamTurn
          : gameData.currentTeamTurn == TeamTurns.teamA
          ? TeamTurns.teamB
          : gameData.currentTeamTurn
        : gameData.currentTeamTurn
    );

    // sounds part
    if (shouldPlaySounds()) {
      const point = getDifficultyInfo(selectedDifficulty, t).point;
      if (isAnsweredCorrectly) {
        if (point == getDifficultyInfo(Difficulty.EASY, t).point) {
          if (selectedLifelineType == LifelineType.TRAP) {
            soundRepo.play(
              getSoundCompletePath(Sounds.correctPit250QuestionGamePops4)
            );
          } else if (selectedLifelineType == LifelineType.DOUBLE_POINT) {
            soundRepo.playWeightedSounds([
              {
                soundPath: getSoundCompletePath(
                  Sounds.correctDoublePoints250QuestionGamePops1
                ),
                weight: 50.0,
              },
              {
                soundPath: getSoundCompletePath(
                  Sounds.correctDoublePoints250QuestionGamePops3
                ),
                weight: 50.0,
              },
            ]);
          } else {
            soundRepo.playWeightedSounds([
              {
                soundPath: getSoundCompletePath(Sounds.correct250QuestionCoin),
                weight: 33.0,
              },
              {
                soundPath: getSoundCompletePath(
                  Sounds.correct250QuestionCorrect
                ),
                weight: 33.0,
              },
              {
                soundPath: getSoundCompletePath(
                  Sounds.correct250QuestionPopBells
                ),
                weight: 34.0,
              },
            ]);
          }
        } else if (point == getDifficultyInfo(Difficulty.MEDIUM, t).point) {
          if (selectedLifelineType == LifelineType.TRAP) {
            soundRepo.playWeightedSounds([
              {
                soundPath: getSoundCompletePath(
                  Sounds.correctPit500QuestionGamePops5
                ),
                weight: 34.0,
              },
              {
                soundPath: getSoundCompletePath(
                  Sounds.correctPit500QuestionStrongPunch2
                ),
                weight: 33.0,
              },
              {
                soundPath: getSoundCompletePath(
                  Sounds.correctPit500QuestionSwordSlashA
                ),
                weight: 33.0,
              },
            ]);
          } else if (selectedLifelineType == LifelineType.DOUBLE_POINT) {
            soundRepo.playWeightedSounds([
              {
                soundPath: getSoundCompletePath(
                  Sounds.correctDoublePoints500QuestionDigitalLoading
                ),
                weight: 34.0,
              },
              {
                soundPath: getSoundCompletePath(
                  Sounds.correctDoublePoints500QuestionGamePops2
                ),
                weight: 33.0,
              },
              {
                soundPath: getSoundCompletePath(
                  Sounds.correctDoublePoints500QuestionDigitalText
                ),
                weight: 33.0,
              },
            ]);
          } else {
            soundRepo.playWeightedSounds([
              {
                soundPath: getSoundCompletePath(
                  Sounds.correct500QuestionAppMoney1
                ),
                weight: 20.0,
              },
              {
                soundPath: getSoundCompletePath(
                  Sounds.correct500QuestionAppMoney2
                ),
                weight: 20.0,
              },
              {
                soundPath: getSoundCompletePath(
                  Sounds.correct500QuestionCashMachine
                ),
                weight: 30.0,
              },
              {
                soundPath: getSoundCompletePath(
                  Sounds.correct500QuestionGlockSuccessDing
                ),
                weight: 30.0,
              },
            ]);
          }
        } else if (point == getDifficultyInfo(Difficulty.HARD, t).point) {
          if (selectedLifelineType == LifelineType.TRAP) {
            soundRepo.playWeightedSounds([
              {
                soundPath: getSoundCompletePath(
                  Sounds.correctPit750QuestionGamePops6
                ),
                weight: 50.0,
              },
              {
                soundPath: getSoundCompletePath(
                  Sounds.correctPit750QuestionPopPoppingDown
                ),

                weight: 50.0,
              },
            ]);
          } else if (selectedLifelineType == LifelineType.DOUBLE_POINT) {
            soundRepo.play(
              getSoundCompletePath(
                Sounds.correctDoublePoints750QuestionCorrectAnswerRings
              )
            );
          } else {
            soundRepo.playWeightedSounds([
              {
                soundPath: getSoundCompletePath(
                  Sounds.correct750QuestionMoneyCoins1
                ),
                weight: 25.0,
              },
              {
                soundPath: getSoundCompletePath(
                  Sounds.correct750QuestionMoneyCoins2
                ),
                weight: 25.0,
              },
              {
                soundPath: getSoundCompletePath(
                  Sounds.correct750QuestionPopWin1
                ),
                weight: 25.0,
              },
              {
                soundPath: getSoundCompletePath(
                  Sounds.correct750QuestionPopWin2
                ),
                weight: 25.0,
              },
            ]);
          }
        }
      } else {
        if (point == getDifficultyInfo(Difficulty.EASY, t).point) {
          soundRepo.playWeightedSounds([
            {
              soundPath: getSoundCompletePath(Sounds.wrong250QuestionFart),
              weight: 20.0,
            },
            {
              soundPath: getSoundCompletePath(Sounds.wrongAnswer2),
              weight: 80.0,
            },
          ]);
        } else {
          soundRepo.playWeightedSounds([
            {
              soundPath: getSoundCompletePath(Sounds.wrongAnswer2),
              weight: 20.0,
            },
            {
              soundPath: getSoundCompletePath(Sounds.wrongAnswer),
              weight: 25.0,
            },
            {
              soundPath: getSoundCompletePath(Sounds.wrongOption1),
              weight: 30.0,
            },
            { soundPath: getSoundCompletePath(Sounds.wrong), weight: 25.0 },
          ]);
        }
      }
    }

    if (gameData.resultGame != null && hasGivenRate) {
      updateResultGame({
        action: "Update HasGivenRate Bool List",
        index: currentQuestionIndexNumber,
      });
    }

    if (question?._id && !alreadyHasQuestion) {
      await addToPlayerQuestionsHistory(question._id);

      await giveAnswerToQuestion(question._id, isAnsweredCorrectly);
    }

    const totalScore = gameData.firstTeamScore + gameData.secondTeamScore;
    if (gameData.resultGame != null && totalScore > gameData.resultGame.score) {
      updateResultGame({
        action: "Update Total Score",
        score: totalScore,
      });
    }

    if (isGameFinished()) {
      navigate(ROUTE_ENDPOINTS.GAME_FINISHED, {
        replace: true,
        state: {
          firstTeamName: gameData.firstTeamName,
          firstTeamScore: gameData.firstTeamScore,
          secondTeamName: gameData.secondTeamName,
          secondTeamScore: gameData.secondTeamScore,
        },
      });
    }
  }

  async function giveAnswerToQuestion(questionId: string, isCorrect: boolean) {
    const response = await questionGiveAnswerUseCase.invoke(
      questionId,
      isCorrect
    );
    switch (response.responseState) {
      case State.Success:
        break;
      case State.Fail:
      case State.Error:
        await giveAnswerToQuestion(questionId, isCorrect);
        break;
    }
  }

  async function addToPlayerQuestionsHistory(questionId: string) {
    const response = await playerAddToHistoryUseCase.invoke(questionId);
    switch (response.responseState) {
      case State.Success:
        break;
      case State.Fail:
      case State.Error:
        await addToPlayerQuestionsHistory(questionId);
        break;
    }
  }

  function isGameFinished(): boolean {
    if (!gameData) return false;
    var result = true;
    if (gameData.categories.length == 0) {
      return false;
    }

    gameData.categories.forEach((category) => {
      var hashmap = category.pointsHashmap;
      hashmap.values().forEach((m) => {
        m.values().forEach((m2) => {
          if (m2 == false) {
            result = false;
          }
        });
      });
    });

    return result;
  }

  function markPointAsCompleted(teamToGivePoint: TeamTurns) {
    if (!gameData || !selectedCategory || !selectedDifficulty) return;
    const categories = [...gameData.categories];
    const index = categories.findIndex((c) => c.id == selectedCategory.id);

    const currentCategory = categories[index];
    const currentTeamDiffMap =
      currentCategory.pointsHashmap.get(teamToGivePoint)!;
    currentTeamDiffMap?.set(selectedDifficulty, true);
    currentCategory.pointsHashmap.set(teamToGivePoint, currentTeamDiffMap);

    updateGameData({
      currentTeamTurn:
        gameData.currentTeamTurn == TeamTurns.teamA
          ? TeamTurns.teamB
          : TeamTurns.teamA,
    });
  }

  function setLoading(loading: boolean) {
    showGlobalLoadingView(loading);
  }

  function onLifelineClicked(lifelineId: string, isFirstTeam: boolean) {
    if (!gameData) return;
    if (
      (gameData.currentTeamTurn == TeamTurns.teamA && !isFirstTeam) ||
      (gameData.currentTeamTurn == TeamTurns.teamB && isFirstTeam)
    ) {
      showSnackbar(t("not_your_turn"));
      return;
    }

    const newList = isFirstTeam
      ? gameData.firstTeamLifelines
      : gameData.secondTeamLifelines;
    const index = newList.findIndex((l) => l.id == lifelineId);
    const currentLifeline = newList[index];
    if (!currentLifeline.shouldChooseBeforeQuestion) {
      showSnackbar(t("can_only_choose_this_lifeline_during_question"));
      return;
    }

    newList.map((l) => {
      if (l.id != lifelineId) l.selected = false;
      else l.selected = !l.selected;
    });

    if (isFirstTeam) {
      updateGameData({ firstTeamLifelines: newList });
    } else {
      updateGameData({ secondTeamLifelines: newList });
    }
  }

  function updateGameData({
    isLoading,
    categories,
    firstTeamName,
    secondTeamName,
    currentTeamTurn,
    firstTeamScore,
    secondTeamScore,
    canAnyTeamChooseAnyQuestion,
    difficultiesMap,
    firstTeamLifelines,
    secondTeamLifelines,
    resultGame,
  }: {
    isLoading?: boolean;
    categories?: Array<Category>;
    firstTeamName?: string;
    secondTeamName?: string;
    currentTeamTurn?: TeamTurns;
    firstTeamScore?: number;
    secondTeamScore?: number;
    canAnyTeamChooseAnyQuestion?: boolean;
    difficultiesMap?: Map<Difficulty, number>;
    firstTeamLifelines?: Array<Lifeline>;
    secondTeamLifelines?: Array<Lifeline>;
    resultGame?: Game;
  }) {
    if (!gameData) return;

    if (isLoading != undefined) {
      gameData.isLoading = isLoading;
    }

    if (categories != undefined) {
      gameData.categories = categories;
    }

    if (firstTeamName != undefined) {
      gameData.firstTeamName = firstTeamName;
    }

    if (secondTeamName != undefined) {
      gameData.secondTeamName = secondTeamName;
    }

    if (currentTeamTurn != undefined) {
      gameData.currentTeamTurn = currentTeamTurn;
    }

    if (firstTeamScore != undefined) {
      gameData.firstTeamScore = firstTeamScore;
    }

    if (secondTeamScore != undefined) {
      gameData.secondTeamScore = secondTeamScore;
    }

    if (canAnyTeamChooseAnyQuestion != undefined) {
      gameData.canAnyTeamChooseAnyQuestion = canAnyTeamChooseAnyQuestion;
    }

    if (difficultiesMap != undefined) {
      gameData.difficultiesMap = difficultiesMap;
    }

    if (firstTeamLifelines != undefined) {
      gameData.firstTeamLifelines = firstTeamLifelines;
    }

    if (secondTeamLifelines != undefined) {
      gameData.secondTeamLifelines = secondTeamLifelines;
    }

    if (resultGame != undefined) {
      gameData.resultGame = resultGame;
    }

    setGameData({ ...gameData });
  }

  function showSnackbar(
    message: string | undefined,
    variant: "error" | "success" = "error"
  ) {
    if (message) enqueueSnackbar(message, { variant: variant });
  }
  return {
    gameData,
    init,
    getRandomQuestion,
    answer,
    question,
    addOrDeductPointsFromATeam,
    onLifelineClicked,
    OnQuestionAnswered,
    hasGivenRate,
    currentQuestionIndexNumber,
    saveGame,
  };
}
